<template>
  <el-dialog
    title="📊 视频答题记录"
    :visible.sync="visible"
    width="80%"
    top="5vh"
    @close="handleClose"
  >
    <!-- 筛选条件 -->
    <div class="filter-bar" style="margin-bottom: 20px;">
      <el-select
        v-model="filterStudentId"
        placeholder="选择学生（全部）"
        clearable
        filterable
        style="width: 200px; margin-right: 10px;"
      >
        <el-option
          v-for="student in students"
          :key="student.id"
          :label="student.username || student.name"
          :value="student.username || student.name"
        />
      </el-select>

      <el-select
        v-model="filterQuestionId"
        placeholder="选择题目（全部）"
        clearable
        style="width: 300px; margin-right: 10px;"
        @change="loadRecords"
      >
        <el-option
          v-for="q in questions"
          :key="q.id"
          :label="`题目${q.id}: ${q.content.substring(0, 30)}...`"
          :value="q.id"
        />
      </el-select>

      <el-button type="primary" icon="el-icon-refresh" @click="loadRecords">刷新</el-button>
      <el-button type="success" icon="el-icon-data-analysis" @click="showStatistics">查看统计</el-button>
    </div>

    <!-- 答题记录表格 -->
    <el-table
      :data="filteredRecords"
      v-loading="loading"
      style="width: 100%"
      stripe
      border
      max-height="500"
    >
      <el-table-column label="学生" width="120" align="center">
        <template slot-scope="{ row }">
          {{ row.username || getStudentName(row.studentId) }}
        </template>
      </el-table-column>
      <el-table-column prop="questionId" label="题目ID" width="80" align="center" />
      <el-table-column label="题目内容" min-width="200">
        <template slot-scope="{row}">
          <div
            style="max-height: 60px; overflow: hidden; text-overflow: ellipsis;"
            v-mathjax
            v-html="formatContent(row.questionContent || '无')"
          />
        </template>
      </el-table-column>
      <el-table-column prop="userAnswer" label="学生答案" width="100" align="center">
        <template slot-scope="{row}">
          <el-tag :type="row.isCorrect ? 'success' : 'danger'" size="medium">
            {{ row.userAnswer }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="correctAnswer" label="正确答案" width="100" align="center" />
      <el-table-column label="是否正确" width="100" align="center">
        <template slot-scope="{row}">
          <el-tag :type="row.isCorrect ? 'success' : 'danger'" effect="dark">
            {{ row.isCorrect ? '✅ 正确' : '❌ 错误' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="attemptCount" label="第几次" width="80" align="center">
        <template slot-scope="{row}">
          <el-tag type="info">第{{ row.attemptCount }}次</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="answerTimeCost" label="用时(秒)" width="90" align="center" />
      <el-table-column label="答题时间" width="160" align="center">
        <template slot-scope="{row}">
          {{ formatTime(row.answerTimestamp) }}
        </template>
      </el-table-column>
    </el-table>

    <!-- 统计对话框 -->
    <el-dialog
      title="📈 答题统计"
      :visible.sync="statisticsVisible"
      width="60%"
      append-to-body
    >
      <!-- 班级筛选 -->
      <div class="stats-filter-bar" style="margin-bottom: 15px;">
        <el-select
          v-model="statsFilterClassId"
          placeholder="选择班级（全部）"
          clearable
          style="width: 200px;"
          @change="loadStatistics"
        >
          <el-option
            v-for="cls in classOptions"
            :key="cls.id"
            :label="cls.name"
            :value="cls.id"
          />
        </el-select>
        <el-button type="primary" icon="el-icon-refresh" size="small" style="margin-left: 10px;" @click="loadStatistics">刷新</el-button>
      </div>
      <el-table
        :data="statistics"
        v-loading="statsLoading"
        style="width: 100%"
        border
      >
        <el-table-column prop="questionId" label="题目ID" width="80" align="center" />
        <el-table-column label="题目内容" min-width="200">
          <template slot-scope="{row}">
            <div
              style="max-height: 60px; overflow: hidden; text-overflow: ellipsis;"
              v-mathjax
              v-html="formatContent(row.questionContent || '无')"
            />
          </template>
        </el-table-column>
        <el-table-column prop="correctAnswer" label="正确答案" width="100" align="center" />
        <el-table-column prop="totalStudents" label="答题人数" width="100" align="center" />
        <el-table-column prop="totalAttempts" label="总答题次数" width="120" align="center" />
        <el-table-column prop="correctCount" label="答对次数" width="100" align="center" />
        <el-table-column prop="correctRate" label="正确率" width="100" align="center">
          <template slot-scope="{row}">
            <el-tag :type="getCorrectRateType(row.correctRate)">
              {{ row.correctRate }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getStudentVideoAnswersByTeacher, getVideoAnswerStatistics } from '@/api/amcVideoEnhancement'

export default {
  name: 'VideoAnswerRecordsDialog',
  directives: {
    mathjax: {
      inserted(el) {
        try {
          if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
            if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
            window.MathJax.typesetPromise([el])
          }
        } catch (_) {}
      },
      componentUpdated(el) {
        try {
          if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
            if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
            window.MathJax.typesetPromise([el])
          }
        } catch (_) {}
      }
    }
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sectionId: {
      type: Number,
      required: false,
      default: null
    },
    students: {
      type: Array,
      default: () => []
    },
    questions: {
      type: Array,
      default: () => []
    },
    classOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      records: [],
      statistics: [],
      loading: false,
      statsLoading: false,
      statisticsVisible: false,
      filterStudentId: null,
      filterQuestionId: null,
      statsFilterClassId: null
    }
  },
  computed: {
    filteredRecords() {
      let list = Array.isArray(this.records) ? this.records.slice() : []
      if (this.filterStudentId) {
        list = list.filter(r => (r.username && r.username === this.filterStudentId))
      }
      if (this.filterQuestionId) {
        list = list.filter(r => String(r.questionId) === String(this.filterQuestionId))
      }
      return list
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadRecords()
      }
    }
  },
  created() {
    this.ensureMathJax()
  },
  methods: {
    ensureMathJax() {
      try {
        if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
          this.typeset()
          return
        }
      } catch (_) {}
      try {
        const vm = this
        if (!window.MathJax || !window.MathJax.startup) {
          window.MathJax = {
            tex: {
              inlineMath: [['$', '$'], ['\\(', '\\)']],
              displayMath: [['$$', '$$'], ['\\[', '\\]']],
              processEscapes: true,
              processEnvironments: true
            },
            svg: { fontCache: 'global' },
            startup: {
              ready: () => {
                try { window.MathJax.startup.defaultReady() } catch (e) {}
                vm.typeset()
              }
            }
          }
        }
      } catch (_) {}
      try {
        const existing = document.querySelector('script[data-mathjax-loaded]')
        if (existing) return
        const vm = this
        const cdns = [
          'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js',
          'https://unpkg.com/mathjax@3/es5/tex-chtml.js',
          'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js'
        ]
        const loadFrom = (idx) => {
          if (idx >= cdns.length) return
          const script = document.createElement('script')
          script.type = 'text/javascript'
          script.src = cdns[idx]
          script.async = true
          script.setAttribute('data-mathjax-loaded', 'true')
          script.onload = () => { vm.typeset && vm.typeset() }
          script.onerror = () => loadFrom(idx + 1)
          document.head.appendChild(script)
        }
        loadFrom(0)
      } catch (_) {}
    },
    async typeset() {
      try {
        await this.$nextTick()
        if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
          const el = this.$el
          if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
          await window.MathJax.typesetPromise([el])
        }
      } catch (_) {}
    },
    getStudentName(studentId) {
      if (!studentId || !Array.isArray(this.students)) return studentId
      const s = this.students.find(st => String(st.id) === String(studentId))
      return (s && (s.name || s.username || s.realName)) || studentId
    },
    formatContent(text) {
      if (!text) return ''
      let s = String(text)
      // 将 \\frac 等双反斜杠还原为单反斜杠，方便 MathJax 识别
      s = s.replace(/\\\\/g, '\\')
      return s.replace(/\n/g, '<br>')
    },
    async loadRecords() {
      if (!this.sectionId) return
      try {
        this.loading = true
        const res = await getStudentVideoAnswersByTeacher(
          this.sectionId,
          null,
          null
        )
        if (res && res.data) {
          this.records = res.data
        } else {
          this.records = []
        }
      } catch (e) {
        console.error('加载答题记录失败', e)
        this.$message.error('加载答题记录失败')
      } finally {
        this.loading = false
      }
    },
    async showStatistics() {
      if (!this.sectionId) return
      this.statisticsVisible = true
      this.statsFilterClassId = null
      await this.loadStatistics()
    },
    async loadStatistics() {
      if (!this.sectionId) return
      try {
        this.statsLoading = true
        const res = await getVideoAnswerStatistics(this.sectionId, this.statsFilterClassId)
        if (res && res.data) {
          this.statistics = res.data
        } else {
          this.statistics = []
        }
      } catch (e) {
        console.error('加载统计数据失败', e)
        this.$message.error('加载统计数据失败')
      } finally {
        this.statsLoading = false
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    getCorrectRateType(rate) {
      const num = parseFloat(rate)
      if (num >= 80) return 'success'
      if (num >= 60) return 'warning'
      return 'danger'
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>
